import FakeLink from "@/components/FakeLink/FakeLink";
import PremiumBanner from "@/components/PremiumFeatureComponents/PremiumBanner";
import Skeleton from "@/components/Skeleton/Skeleton";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import { useCompanyAccess, useCompanySettings } from "@/hooks/useCompanySettings";
import { useGetFacility, usePublishFacility, useUnPublishFacility } from "@/hooks/useFacilities";
import { useUserSettings } from "@/hooks/useUserSettings";
import { useDashboardYear } from "@/pages/dashboard/hooks/useDateRange";
import {
  DownOutlined,
  ExportOutlined,
  FileTextOutlined,
  GlobalOutlined,
  LinkOutlined,
  LockFilled,
  UnlockFilled,
  UpOutlined,
} from "@ant-design/icons";
import { Button, Col, Flex, Input, message, Popover, Row, Space, Tooltip, Typography } from "antd";
import { useState } from "react";
import styles from "./PublishFacility.module.scss";

const { Text } = Typography;

function PublishFacility({ facilityId, disabled }: { facilityId: number; disabled?: boolean }) {
  const [open, setOpen] = useState(false);

  const { data: facility, isLoading: getFacilityLoading } = useGetFacility(
    facilityId,
    !isNaN(facilityId)
  );
  const access = useCompanyAccess();
  const { year } = useDashboardYear();
  const [isRequestEmailOpen, setIsRequestEmailOpen] = useState(false);

  const publishFacilityMutation = usePublishFacility();
  const unPublishFacilityMutation = useUnPublishFacility();
  const noAccess = access && !access.includes("supplier_facility_invite");

  const loading =
    getFacilityLoading || publishFacilityMutation.isLoading || unPublishFacilityMutation.isLoading;

  const publishUrl =
    window.location.origin + "/share/facility/" + facility?.published_id + `?year=${year}`;

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
  };

  const onPublishChange = (value: boolean) => {
    if (value) {
      publishFacilityMutation
        .mutateAsync({
          facilityId,
        })
        .then(() => {
          message.success("Facility successfully published.");
        })
        .catch((data) => {
          message.error(data?.response.data.message);
        });
    } else {
      unPublishFacilityMutation
        .mutateAsync({
          facilityId,
        })
        .then(() => {
          message.success("Facility successfully unpublished.");
        })
        .catch((data) => {
          message.error(data?.response.data.message);
        });
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(publishUrl);
    message.success("URL Copied to clipboard");
  };

  const suffix = <FakeLink onClick={copyToClipboard}>copy</FakeLink>;

  const company = useCompanySettings();
  const user = useUserSettings();

  const copyRequestEmailToClipboard = () => {
    navigator.clipboard.writeText(`Dear [Supplier Name],

As part of our ongoing efforts to reduce the environmental footprint of our products, we are working closely with our suppliers to gain better visibility into resource use across our supply chain.

We are using a dedicated platform developed by Root Sustainability, which allows us to collect and assess key environmental data in a structured and secure way. This will support compliance with upcoming regulations and help identify opportunities for efficiency and sustainability improvements.

We kindly ask you to provide the following information for your facility (or facilities). Please make sure that the data that you provide is limited to consumption associated to the products produced for ${company.data?.company_name}. If your facility produces for more clients than ${company.data?.company_name}, you can use mass allocation to calculate the consumption associated to the products produced for ${company.data?.company_name}.

* Electricity consumption (annual, in kWh), limited to production for ${company.data?.company_name}
* Heating/fuel use (type and amount, e.g., natural gas, biomass, etc.), limited to production for ${company.data?.company_name}
* Water consumption (annual, in m³), limited to production for ${company.data?.company_name}
* Cooling
* Consumables
* Residual waste 

Please submit this information via the following secure link:

${publishUrl}

The link contains a secure environment where consumption data for your specific facility can be filled out.

If you have any questions or need support, please reach out to us directly at ${user.data?.email}.

Thank you very much for your contribution and collaboration.

Best regards,`);
    message.success("Email template copied to clipboard");
  };

  const copyRequestEmail = <FakeLink onClick={copyRequestEmailToClipboard}>copy</FakeLink>;

  const openInNewTab = () => {
    window.open(publishUrl, "_blank");
  };

  const PopOverContent = () => (
    <Row gutter={[0, 12]}>
      <Col span={24}>
        <span className={styles.subText}>
          Publish your unique url below and share it with the manager of this facility to allow them
          to provide their data into Root.
        </span>
      </Col>
      <Col span={24}>
        <Space>
          <Tooltip
            placement="bottom"
            title={
              disabled && !noAccess
                ? "In order to publish this facility, you need to enter a valid address."
                : undefined
            }
          >
            <>
              <SwitchWithLabel
                size={"small"}
                onChange={onPublishChange}
                checked={facility?.published_id !== null}
                loading={loading}
                disabled={disabled || noAccess}
              >
                <span className={styles.mainText}>Publish URL</span>
              </SwitchWithLabel>
            </>
          </Tooltip>
        </Space>
      </Col>
      <Col span={facility?.published_id === null ? 24 : 0}>
        <Space className={styles.subText}>
          <LockFilled />
          <span>Only logged in users from your organisation can view and edit. </span>
        </Space>
      </Col>
      <Col span={facility?.published_id !== null ? 24 : 0}>
        <Space className={styles.subText}>
          <UnlockFilled />
          <span>Anyone you share this unique URL with can view and edit the data. </span>
        </Space>
      </Col>
      <Col span={facility?.published_id !== null ? 24 : 0}>
        <Space.Compact style={{ width: "100%" }}>
          <Input
            size="small"
            suffix={suffix}
            disabled={true}
            value={publishUrl}
            prefix={<LinkOutlined />}
            className={styles.publishIdInput}
            style={{ textOverflow: "ellipsis" }}
          />
          <Button
            icon={<ExportOutlined />}
            onClick={openInNewTab}
          />
        </Space.Compact>
      </Col>
      <Col span={facility?.published_id !== null ? 24 : 0}>
        <Row
          className={[styles.template, isRequestEmailOpen ? styles.open : styles.closed].join(" ")}
        >
          <Col span={24}>
            <Flex
              gap={4}
              align={"center"}
              justify="space-between"
              className={styles.header}
            >
              <Flex align={"center"}>
                <FileTextOutlined className={styles.fileIcon} />
                <Text
                  className={styles.clickable}
                  onClick={() => setIsRequestEmailOpen(!isRequestEmailOpen)}
                >
                  Data request email template
                </Text>
              </Flex>
              <Flex
                align={"center"}
                gap={8}
              >
                {copyRequestEmail}
                <span
                  className={styles.clickable}
                  onClick={() => setIsRequestEmailOpen(!isRequestEmailOpen)}
                >
                  {isRequestEmailOpen ? (
                    <UpOutlined className={styles.upIcon} />
                  ) : (
                    <DownOutlined className={styles.upIcon} />
                  )}
                </span>
              </Flex>
            </Flex>
          </Col>
          <Col
            span={24}
            className={styles.content}
          >
            <Flex
              gap={4}
              vertical
            >
              <Text strong>We've prepared an email template to make things easier for you.</Text>
              <Text>
                You can copy and paste this template into a new e-mail addressed to the facility
                contact or manager. The template already includes the URL listed above. Feel free to
                adapt the wording as needed before sending.
              </Text>
            </Flex>
          </Col>
        </Row>
      </Col>
      {noAccess && (
        <Col span={24}>
          <PremiumBanner />
        </Col>
      )}
    </Row>
  );

  return (
    <>
      {getFacilityLoading ? (
        <Skeleton style={{ width: 101, height: 32 }} />
      ) : (
        <Popover
          content={<PopOverContent />}
          title="Share and request data directly from the facility manager"
          trigger="click"
          open={open}
          onOpenChange={handleOpenChange}
          placement={"bottomLeft"}
          arrow={false}
          rootClassName={styles.container}
        >
          <>
            <Button
              icon={
                <GlobalOutlined
                  style={facility?.published_id !== null ? { color: "#52C41A" } : {}}
                />
              }
            >
              {facility?.published_id !== null ? "Published" : "Publish"}
            </Button>
          </>
        </Popover>
      )}
    </>
  );
}

export default PublishFacility;
